import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";

function ResetPassword() {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get("token");
    if (!token) {
      setMessage("Invalid or expired reset link.");
    }
  }, [location]);

  const handlePasswordReset = async () => {
    if (newPassword !== confirmPassword) {
      setMessage("Passwords do not match.");
      return;
    }

    if (newPassword.length < 8) {
      setMessage("Password must be at least 8 characters long.");
      return;
    }

    setIsLoading(true);
    setMessage("");

    try {
      const queryParams = new URLSearchParams(location.search);
      const token = queryParams.get("token");

      if (!token) {
        setMessage("Invalid or expired reset link.");
        setIsLoading(false);
        return;
      }

      const response = await axios.post("/api/auth/reset-password", {
        token,
        newPassword,
      });

      // Success!
      setMessage("Password reset successfully! Redirecting to login...");

      // CRITICAL: Clear all local tokens so no stale refresh token is used
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");

      // Redirect after short delay so user sees the success message
      setTimeout(() => {
        navigate("/login");
      }, 2500); // 2.5 seconds

    } catch (error) {
      const errMsg = error.response?.data?.msg || "Error resetting password. The link may have expired.";
      setMessage(errMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <Helmet>
        <title>Reset Password - Sintrix.net</title>
        <meta
          name="description"
          content="Reset your password for your Sintrix.net account. Enter a new password to regain access."
        />
      </Helmet>

      <div style={styles.formContainer}>
        <h2 style={styles.title}>Reset Password</h2>

        {message && (
          <p
            style={
              message.includes("successfully")
                ? styles.successMessage
                : styles.errorMessage
            }
          >
            {message}
          </p>
        )}

        <input
          type="password"
          placeholder="New Password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          style={styles.input}
          disabled={isLoading}
        />
        <input
          type="password"
          placeholder="Confirm New Password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          style={styles.input}
          disabled={isLoading}
        />

        <button
          onClick={handlePasswordReset}
          style={styles.button}
          disabled={isLoading}
        >
          {isLoading ? "Resetting..." : "Reset Password"}
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    backgroundColor: "var(--background-color)",
  },
  formContainer: {
    backgroundColor: "var(--card-background)",
    padding: "40px 20px",
    borderRadius: "16px",
    boxShadow: "0 10px 20px rgba(0, 0, 0, 0.1)",
    width: "100%",
    maxWidth: "400px",
    textAlign: "center",
  },
  title: {
    color: "var(--text-color)",
    marginBottom: "20px",
    fontWeight: "bold",
  },
  input: {
    padding: "12px",
    width: "100%",
    margin: "10px 0",
    borderRadius: "6px",
    border: `1px solid var(--border-color)`,
    fontSize: "16px",
    backgroundColor: "var(--input-background)",
    color: "var(--input-text)",
  },
  button: {
    padding: "12px 20px",
    backgroundColor: "var(--button-background)",
    color: "var(--button-text)",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    width: "100%",
    marginTop: "10px",
    fontSize: "16px",
    transition: "background-color 0.3s ease",
  },
  errorMessage: {
    color: "var(--error-color)",
    marginBottom: "10px",
  },
  successMessage: {
    color: "var(--success-color)",
    marginBottom: "10px",
  },
};

export default ResetPassword;